import React from "react";

/**
 * SheetChart — renders a bar / line / pie chart as pure SVG (no library) from
 * { labels: string[], series: [{ name, values:number[] }] }. Used by the Sheets
 * app to visualize a selected range.
 */
const PALETTE = ["#1d6fe0", "#16a34a", "#db2777", "#f59e0b", "#7c3aed", "#0891b2", "#dc2626", "#65a30d"];
const W = 360;
const H = 210;
const PAD = { l: 34, r: 10, t: 10, b: 28 };
const plotW = W - PAD.l - PAD.r;
const plotH = H - PAD.t - PAD.b;

const niceMax = (m) => {
  if (m <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(m));
  return Math.ceil(m / pow) * pow;
};

const BarChart = ({ labels, series }) => {
  const max = niceMax(Math.max(1, ...series.flatMap((s) => s.values.map((v) => Math.max(0, v)))));
  const groups = labels.length || 1;
  const gw = plotW / groups;
  const bw = Math.max(2, (gw * 0.7) / series.length);
  return (
    <g>
      {[0, 0.5, 1].map((t) => (
        <g key={t}>
          <line x1={PAD.l} x2={W - PAD.r} y1={PAD.t + plotH * (1 - t)} y2={PAD.t + plotH * (1 - t)} stroke="#eceef1" />
          <text x={PAD.l - 4} y={PAD.t + plotH * (1 - t) + 3} textAnchor="end" fontSize="8" fill="#9aa1ab">
            {Math.round(max * t)}
          </text>
        </g>
      ))}
      {labels.map((lab, gi) =>
        series.map((s, si) => {
          const v = Math.max(0, s.values[gi] || 0);
          const h = (v / max) * plotH;
          const x = PAD.l + gi * gw + gw * 0.15 + si * bw;
          return <rect key={`${gi}-${si}`} x={x} y={PAD.t + plotH - h} width={bw - 1} height={h} fill={PALETTE[si % PALETTE.length]} />;
        })
      )}
      {labels.map((lab, gi) => (
        <text key={gi} x={PAD.l + gi * gw + gw / 2} y={H - PAD.b + 12} textAnchor="middle" fontSize="8" fill="#5b6470">
          {String(lab).slice(0, 8)}
        </text>
      ))}
    </g>
  );
};

const LineChart = ({ labels, series }) => {
  const all = series.flatMap((s) => s.values);
  const max = niceMax(Math.max(1, ...all));
  const min = Math.min(0, ...all);
  const span = max - min || 1;
  const n = labels.length || 1;
  const x = (i) => PAD.l + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v) => PAD.t + plotH - ((v - min) / span) * plotH;
  return (
    <g>
      {[0, 0.5, 1].map((t) => (
        <line key={t} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + plotH * t} y2={PAD.t + plotH * t} stroke="#eceef1" />
      ))}
      {series.map((s, si) => (
        <polyline
          key={si}
          fill="none"
          stroke={PALETTE[si % PALETTE.length]}
          strokeWidth="2"
          points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
        />
      ))}
      {labels.map((lab, i) => (
        <text key={i} x={x(i)} y={H - PAD.b + 12} textAnchor="middle" fontSize="8" fill="#5b6470">
          {String(lab).slice(0, 8)}
        </text>
      ))}
    </g>
  );
};

const PieChart = ({ labels, series }) => {
  const vals = (series[0] ? series[0].values : []).map((v) => Math.max(0, v));
  const total = vals.reduce((s, v) => s + v, 0) || 1;
  const cx = PAD.l + plotW / 2;
  const cy = PAD.t + plotH / 2;
  const rad = Math.min(plotW, plotH) / 2 - 4;
  let a0 = -Math.PI / 2;
  return (
    <g>
      {vals.map((v, i) => {
        const a1 = a0 + (v / total) * Math.PI * 2;
        const large = a1 - a0 > Math.PI ? 1 : 0;
        const p = `M${cx},${cy} L${cx + rad * Math.cos(a0)},${cy + rad * Math.sin(a0)} A${rad},${rad} 0 ${large} 1 ${cx + rad * Math.cos(a1)},${cy + rad * Math.sin(a1)} Z`;
        a0 = a1;
        return <path key={i} d={p} fill={PALETTE[i % PALETTE.length]} stroke="#fff" strokeWidth="1" />;
      })}
    </g>
  );
};

const SheetChart = ({ type, labels, series }) => {
  const safeLabels = labels && labels.length ? labels : ["—"];
  const safeSeries = series && series.length ? series : [{ name: "Series 1", values: [0] }];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="webtop-sheet__chartsvg" preserveAspectRatio="xMidYMid meet">
      {type === "pie" ? (
        <PieChart labels={safeLabels} series={safeSeries} />
      ) : type === "line" ? (
        <LineChart labels={safeLabels} series={safeSeries} />
      ) : (
        <BarChart labels={safeLabels} series={safeSeries} />
      )}
      {/* legend */}
      {(type === "pie" ? safeLabels : safeSeries).map((it, i) => (
        <g key={i} transform={`translate(${PAD.l + i * 70}, ${H - 6})`}>
          <rect width="8" height="8" y="-7" fill={PALETTE[i % PALETTE.length]} />
          <text x="11" y="0" fontSize="8" fill="#5b6470">
            {String(type === "pie" ? it : it.name).slice(0, 9)}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default SheetChart;
